import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../config";
import {
  TrendingUp, Search, Eye, Mic, Globe, Shield, CheckCircle,
  ArrowRight, MapPin, Zap, ChevronRight
} from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";

const STATS = [
  { val: "73%", labelKey: "ls_stat1", fallback: "sites invisible to AI", color: "#a855f7" },
  { val: "2,400", labelKey: "ls_stat2", fallback: "avg visits from AI search", color: "#10B981" },
  { val: "30s", labelKey: "ls_stat3", fallback: "free audit results", color: "#FFE600" },
];

export default function LocalSEOPage() {
  const { lang, city } = useParams();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [url, setUrl] = useState("");

  useEffect(() => {
    const endpoint = city
      ? `${API}/seo/local/${lang}/${city}`
      : `${API}/seo/local/${lang}`;
    axios.get(endpoint).then(r => setData(r.data)).catch(() => {});
  }, [lang, city]);

  if (!data) return (
    <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#a855f7] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const pageTitle = city ? data.h1 : `SEO & AI Visibility — ${data.country}`;
  const pageDesc = city ? data.desc : `Free AI SEO audit for businesses in ${data.country}. Check your AI visibility now.`;

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">Home</a>
            <a href="/blog" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">Blog</a>
            <a href="/plugin" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">Plugin</a>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-4" data-testid="breadcrumb">
        <nav className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] font-['JetBrains_Mono']">
          <a href="/" className="hover:text-[var(--color-text)]">Home</a>
          <ChevronRight className="w-3 h-3" />
          <a href={`/seo/${lang}`} className="hover:text-[var(--color-text)]">{data.country}</a>
          {city && data.city && <>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[var(--color-text)]">{data.city}</span>
          </>}
        </nav>
      </div>

      {/* Hero */}
      <section className="py-20 px-6" data-testid="local-hero">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <MapPin className="w-5 h-5 text-[#a855f7]" />
            <span className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.2em] uppercase">
              {data.country} {city && `/ ${data.city}`}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.9] mb-6" data-testid="local-h1">
            {pageTitle}
          </h1>
          <p className="text-lg text-[var(--color-text-muted)] mb-10 max-w-2xl mx-auto">{pageDesc}</p>

          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-4">
            <input
              data-testid="local-url-input"
              type="text" value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder={city ? `Enter your ${data.city} website URL...` : "Enter your website URL..."}
              className="flex-1 px-6 py-4 bg-[var(--color-surface)] border-2 border-[var(--color-border)] text-white text-lg font-['JetBrains_Mono'] focus:border-[#a855f7] focus:outline-none placeholder:text-[var(--color-text-muted)]"
            />
            <a
              href={url ? `/?url=${encodeURIComponent(url)}` : "/"}
              className="px-8 py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 transition-all flex items-center justify-center gap-3"
              data-testid="local-scan-btn"
            >
              <Search className="w-5 h-5" />SCAN FREE
            </a>
          </div>
          <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">No credit card. Instant results. 26 languages.</p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="text-center" data-testid={`local-stat-${i}`}>
              <p className="text-4xl font-black font-['JetBrains_Mono']" style={{color: s.color}}>{s.val}</p>
              <p className="text-[var(--color-text-muted)] text-sm mt-1">{t[s.labelKey] || s.fallback}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12" data-testid="local-h2">
            {city ? data.h2 : `SEO Tools for ${data.country}`}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: Eye, title: t.ls_f1_t || "AI Visibility Tracking", desc: t.ls_f1_d || "Check if ChatGPT, Perplexity & Gemini cite your website" },
              { icon: Search, title: t.ls_f2_t || "Complete SEO Audit", desc: t.ls_f2_d || "Technical analysis with actionable recommendations" },
              { icon: Mic, title: t.ls_f3_t || "Voice Search Score", desc: t.ls_f3_d || "Optimize for Siri, Alexa, Google Assistant" },
              { icon: Globe, title: t.ls_f4_t || "GEO Optimization", desc: `${t.ls_f4_d || "Local SEO for"} ${city ? data.city : data.country}` },
              { icon: Shield, title: t.ls_f5_t || "Backlink Analysis", desc: t.ls_f5_d || "Check dofollow, nofollow & domain authority" },
              { icon: Zap, title: t.ls_f6_t || "Site Speed", desc: t.ls_f6_d || "Core Web Vitals & performance score" },
            ].map((s, i) => (
              <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 hover:border-[var(--color-primary)] transition-colors">
                <s.icon className="w-6 h-6 text-[var(--color-text-muted)] mb-3" />
                <h3 className="font-bold text-white font-['Cabinet_Grotesk'] mb-1">{s.title}</h3>
                <p className="text-sm text-[var(--color-text-muted)]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* City List */}
      {data.cities && data.cities.length > 0 && (
        <section className="py-20 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="city-list">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-8">
              {city ? `${t.ls_more_cities || "More cities in"} ${data.country}` : `${t.ls_top_cities || "Top cities in"} ${data.country}`}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {data.cities.map((c, i) => {
                const slug = c.toLowerCase().replace(/ /g, "-").replace(/[ãöüäéèøåžšččřőűńłșțğı]/g, m => ({ã:"a",ö:"o",ü:"u",ä:"a",é:"e",è:"e",ø:"o",å:"a",ž:"z",š:"s",č:"c",ř:"r",ő:"o",ű:"u",ń:"n",ł:"l",ș:"s",ț:"t",ğ:"g",ı:"i"}[m] || m));
                return (
                  <Link
                    key={i}
                    to={`/seo/${lang}/${slug}`}
                    className="bg-[var(--color-bg)] border border-[var(--color-border)] p-4 text-center hover:border-[#a855f7] transition-colors group"
                    data-testid={`city-link-${i}`}
                  >
                    <MapPin className="w-4 h-4 text-[var(--color-text-muted)] group-hover:text-[#a855f7] mx-auto mb-2 transition-colors" />
                    <span className="text-sm font-bold text-[var(--color-text)] group-hover:text-[var(--color-text)]">{c}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.ls_cta_h1 || "START YOUR FREE"} <span className="text-[#a855f7]">{t.ls_cta_h2 || "SEO AUDIT"}</span>
          </h2>
          <p className="text-[var(--color-text-muted)] mb-8">
            {city ? `${t.ls_cta_join || "Join businesses in"} ${data.city} ${t.ls_cta_using || "using TopRankPulse."}` : `${t.ls_cta_join || "Join businesses in"} ${data.country} ${t.ls_cta_using || "using TopRankPulse."}`}
          </p>
          <a href="/" className="inline-flex items-center gap-3 px-10 py-5 bg-[#a855f7] text-white font-black text-xl uppercase tracking-wider hover:bg-purple-600 transition-all" data-testid="local-cta">
            <Search className="w-6 h-6" />{t.ls_scan || "SCAN YOUR WEBSITE"}
          </a>
          <div className="flex justify-center gap-8 mt-8 text-[var(--color-text-muted)] text-sm">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" />{t.ls_free || "Free forever"}</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4" />{t.ls_no_card || "No credit card"}</span>
            <span className="flex items-center gap-2"><Globe className="w-4 h-4" />{t.ls_langs || "26 languages"}</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
